import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { useLightbox } from "@/components/Lightbox";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge, SyncBadge, NumberField, TriState, BoolSwitch, PrestIcon } from "@/components/common";
import {
  PRESTATIONS, PRESTATION_LIST, ACCESS_MEANS, surfaceVitres, surfaceSols, ligneSurface, fmtNum, fmtDate, missingPoints,
} from "@/lib/helpers";
import { DatePicker } from "@/components/DatePicker";
import { DictationDialog } from "@/components/DictationDialog";
import { VisitReportCard } from "@/components/VisitReportCard";
import { SurfaceZoneCard, SurfaceTools, newSurfaceZone } from "@/components/SurfaceZoneCard";
import * as offlineSync from "@/lib/offlineSync";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const STEPS = [
  { id: "site", label: "1. Site" },
  { id: "releves", label: "2. Relevés" },
  { id: "contraintes", label: "3. Contraintes & Photos" },
  { id: "synthese", label: "4. Synthèse" },
];

function newZone(prestation) {
  if (PRESTATIONS[prestation]?.surface) return newSurfaceZone(prestation);
  return { id: crypto.randomUUID(), name: "", prestation, lignes: [{}], observations: "", photo_ids: [] };
}

// ---------- Generic field renderer ----------
function FieldRenderer({ field, value, onChange, disabled, testId }) {
  if (field.type === "number")
    return <NumberField label={field.label} unit={field.unit} integer={field.integer}
      value={value} onChange={onChange} testId={testId} placeholder={field.placeholder} />;
  if (field.type === "boolean")
    return <BoolSwitch label={field.label} value={value} onChange={onChange} testId={testId} />;
  if (field.type === "select")
    return (
      <div className="space-y-1.5">
        <Label className="text-xs text-slate-600">{field.label}</Label>
        <Select value={value != null ? String(value) : ""} onValueChange={(v) => onChange(field.options[0]?.v !== undefined && typeof field.options[0].v === "number" ? Number(v) : v)} disabled={disabled}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => {
              const val = typeof o === "object" ? o.v : o;
              const lab = typeof o === "object" ? o.l : o;
              return <SelectItem key={String(val)} value={String(val)}>{lab}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
    );
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-600">{field.label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        placeholder={field.placeholder} data-testid={testId} />
    </div>
  );
}

// ---------- Zone card ----------
function ZoneCard({ zone, def, disabled, onChange, onDuplicate, onDelete, onDictate }) {
  const setLigne = (idx, key, val) => {
    const lignes = zone.lignes.map((l, i) => (i === idx ? { ...l, [key]: val } : l));
    onChange({ ...zone, lignes });
  };
  const addLigne = () => onChange({ ...zone, lignes: [...zone.lignes, {}] });
  const removeLigne = (idx) => onChange({ ...zone, lignes: zone.lignes.filter((_, i) => i !== idx) });

  const zoneSurface = def.compute
    ? zone.lignes.reduce((acc, l) => { const s = ligneSurface(def.compute, l); return s == null ? acc : acc + s; }, 0)
    : null;

  return (
    <Card className="p-4" data-testid={`zone-card-${zone.id}`}>
      <div className="flex items-center gap-2">
        <Input value={zone.name} onChange={(e) => onChange({ ...zone, name: e.target.value })} disabled={disabled}
          placeholder="Nom de la zone (ex. Hall d'entrée)" className="font-medium" data-testid={`zone-name-${zone.id}`} />
        {!disabled && (
          <>
            <Button variant="ghost" size="icon" onClick={onDictate} title="Dicter mon relevé" data-testid={`zone-dictate-${zone.id}`}><Icons.Mic className="h-4 w-4 text-emerald-600" /></Button>
            <Button variant="ghost" size="icon" onClick={onDuplicate} title="Dupliquer" data-testid={`zone-duplicate-${zone.id}`}><Icons.Copy className="h-4 w-4 text-slate-500" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="ghost" size="icon" title="Supprimer" data-testid={`zone-delete-${zone.id}`}><Icons.Trash2 className="h-4 w-4 text-rose-500" /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Supprimer cette zone ?</AlertDialogTitle>
                  <AlertDialogDescription>« {zone.name || "Zone sans nom"} » et ses relevés seront définitivement supprimés.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-zone-${zone.id}`}>Supprimer</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>

      <div className="mt-4 space-y-4">
        {zone.lignes.map((ligne, idx) => {
          const sv = def.compute ? ligneSurface(def.compute, ligne) : null;
          return (
            <div key={idx} className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
              {def.multiLine && (
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{def.lineLabel} {idx + 1}</span>
                  {!disabled && zone.lignes.length > 1 && (
                    <button onClick={() => removeLigne(idx)} className="text-xs text-rose-500 hover:underline" data-testid={`remove-ligne-${zone.id}-${idx}`}>Retirer</button>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {def.fields.map((f) => (
                  <FieldRenderer key={f.key} field={f} value={ligne[f.key]} disabled={disabled}
                    onChange={(v) => setLigne(idx, f.key, v)} testId={`field-${zone.id}-${idx}-${f.key}`} />
                ))}
              </div>
              {def.compute && (
                <div className="mt-2 text-sm">
                  {sv == null
                    ? <span className="inline-flex items-center gap-1 text-amber-600" data-testid={`surface-incomplete-${zone.id}-${idx}`}><Icons.AlertTriangle className="h-3.5 w-3.5" /> Relevé incomplet — dimension manquante</span>
                    : <span className="font-mono-data font-medium text-emerald-700" data-testid={`surface-${zone.id}-${idx}`}>Surface calculée : {fmtNum(sv)} m²</span>}
                </div>
              )}
            </div>
          );
        })}
        {def.multiLine && !disabled && (
          <Button variant="outline" size="sm" onClick={addLigne} data-testid={`add-ligne-${zone.id}`}>
            <Icons.Plus className="mr-1.5 h-4 w-4" /> Ajouter un {def.lineLabel}
          </Button>
        )}
      </div>

      {zoneSurface != null && zoneSurface > 0 && (
        <div className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
          Total zone : <span className="font-mono-data" data-testid={`zone-total-${zone.id}`}>{fmtNum(zoneSurface)} m²</span>
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        <Label className="text-xs text-slate-600">Observations de la zone</Label>
        <Textarea value={zone.observations || ""} onChange={(e) => onChange({ ...zone, observations: e.target.value })}
          disabled={disabled} rows={2} data-testid={`zone-obs-${zone.id}`} />
      </div>
    </Card>
  );
}

export default function VisitEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const canManage = hasPerm("visits.manage");
  const [visit, setVisit] = useState(null);
  const [step, setStep] = useState("site");
  const [activePrest, setActivePrest] = useState(null);
  const [sync, setSync] = useState("synced");
  const [commercials, setCommercials] = useState([]);
  const [catalog, setCatalog] = useState({ custom_prestations: [], equipments: [] });
  const [reviewComment, setReviewComment] = useState("");
  const [completionReply, setCompletionReply] = useState("");
  const timer = useRef(null);
  const uploadRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [photoZone, setPhotoZone] = useState("");
  const lb = useLightbox();
  const baseRef = useRef(null);
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(0);
  const [dictateCtx, setDictateCtx] = useState(null);

  const draftKey = `signa_draft_${id}`;

  useEffect(() => {
    api.get(`/visits/${id}`).then((r) => {
      setVisit(r.data);
      baseRef.current = r.data.updated_at;
      setActivePrest(r.data.prestations?.[0] || null);
    }).catch((e) => {
      const local = localStorage.getItem(draftKey);
      if (local) { const d = JSON.parse(local); setVisit(d); baseRef.current = d.updated_at; toast.warning("Mode hors ligne : brouillon local chargé"); }
      else { toast.error(apiError(e)); navigate("/check"); }
    });
    if (canManage) api.get("/users").then((r) => setCommercials(r.data.filter((u) => u.role === "commercial" || u.role === "admin")));
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  // Synchronisation hors ligne : file d'attente, resynchro auto, détection de conflits.
  useEffect(() => {
    offlineSync.setSavedHandler((vid, data) => {
      if (vid !== id) return;
      baseRef.current = data.updated_at;
      localStorage.removeItem(draftKey);
    });
    offlineSync.setConflictHandler((vid) => { if (vid === id) { setConflict(true); setSync("conflict"); } });
    offlineSync.setFlushedHandler((vid) => {
      if (vid !== id) return;
      api.get(`/visits/${id}`).then((r) => setVisit((p) => ({ ...p, photos: r.data.photos })));
    });
    const unsub = offlineSync.subscribe((q) => {
      const pend = q.filter((i) => i.visitId === id).length;
      setPending(pend);
      setSync((s) => (s === "conflict" ? s : pend > 0 ? (offlineSync.isOnline() ? "syncing" : "offline") : "synced"));
    });
    const goOnline = () => { offlineSync.flush(); };
    const goOffline = () => setSync((s) => (s === "conflict" ? s : "offline"));
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      unsub();
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      offlineSync.setSavedHandler(null); offlineSync.setConflictHandler(null); offlineSync.setFlushedHandler(null);
    };
    // eslint-disable-next-line
  }, [id]);

  const readOnly = visit && !canManage && ["validee", "a_verifier"].includes(visit.status);

  const allPrestations = useMemo(() => {
    const m = { ...PRESTATIONS };
    (catalog.custom_prestations || []).forEach((p) => {
      m[p.id] = { ...PRESTATIONS.autre, id: p.id, name: p.name, icon: "SquarePen" };
    });
    return m;
  }, [catalog]);
  const allPrestationList = useMemo(() => Object.values(allPrestations), [allPrestations]);
  const equipments = visit?.equipments_used || [];
  const toggleEquip = (name) => {
    const has = equipments.includes(name);
    update({ equipments_used: has ? equipments.filter((e) => e !== name) : [...equipments, name] });
  };

  const buildPayload = (v) => ({
    date: v.date, scheduled_time: v.scheduled_time, prestations: v.prestations, zones: v.zones,
    constraints: v.constraints, observations: v.observations,
    internal_notes: v.internal_notes, info_to_confirm: v.info_to_confirm,
    equipments_used: v.equipments_used || [],
  });

  const persist = useCallback((next) => {
    localStorage.setItem(draftKey, JSON.stringify(next));
    setSync(offlineSync.isOnline() ? "syncing" : "offline");
    offlineSync.enqueueVisitUpdate(id, buildPayload(next), baseRef.current);
  }, [id, draftKey]);

  const resolveKeepMine = async () => {
    try {
      const { data } = await api.put(`/visits/${id}`, buildPayload(visit)); // sans base_updated_at → force
      baseRef.current = data.updated_at; setConflict(false); setSync("synced"); localStorage.removeItem(draftKey);
      toast.success("Votre version a été conservée");
    } catch (e) { toast.error(apiError(e)); }
  };
  const resolveUseServer = async () => {
    try {
      const { data } = await api.get(`/visits/${id}`);
      setVisit(data); baseRef.current = data.updated_at; setConflict(false); setSync("synced");
      toast.success("Version du serveur rechargée");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportSynthesis = async () => {
    try {
      const { data } = await api.get(`/visits/${id}/synthesis.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `${visit.reference}_synthese.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Synthèse exportée (tableur)");
    } catch (e) { toast.error(apiError(e)); }
  };

  const fileToDataUrl = (file) => new Promise((res) => { const r = new FileReader(); r.onload = (e) => res(e.target.result); r.readAsDataURL(file); });

  const update = (patch) => {
    setVisit((prev) => {
      const next = { ...prev, ...patch };
      if (!readOnly) {
        setSync("local");
        clearTimeout(timer.current);
        timer.current = setTimeout(() => persist(next), 900);
      }
      return next;
    });
  };

  if (!visit) return <div className="flex h-[60vh] items-center justify-center text-slate-400"><Icons.Loader2 className="mr-2 h-5 w-5 animate-spin" />Chargement de la visite…</div>;

  // ----- prestations / zones helpers -----
  const togglePrestation = (pid) => {
    const has = visit.prestations.includes(pid);
    const prestations = has ? visit.prestations.filter((p) => p !== pid) : [...visit.prestations, pid];
    update({ prestations });
    if (!has) setActivePrest(pid);
  };
  const zonesFor = (pid) => (visit.zones || []).filter((z) => z.prestation === pid);
  const setZones = (zones) => update({ zones });
  const addZone = (pid) => { setZones([...(visit.zones || []), newZone(pid)]); };
  const changeZone = (zoneId, next) => setZones(visit.zones.map((z) => (z.id === zoneId ? next : z)));
  const duplicateZone = (zone) => setZones([...visit.zones, { ...zone, id: crypto.randomUUID(), name: `${zone.name} (copie)`, photo_ids: [] }]);
  const deleteZone = (zoneId) => setZones(visit.zones.filter((z) => z.id !== zoneId));

  const applyDictation = (res) => {
    if (!res) return;
    const merge = (ligne) => { const n = { ...ligne }; Object.entries(res.ligne || {}).forEach(([k, v]) => { n[k] = v; }); return n; };
    if (dictateCtx?.mode === "zone") {
      const zone = visit.zones.find((z) => z.id === dictateCtx.zoneId);
      if (!zone) return;
      const lignes = zone.lignes?.length ? [...zone.lignes] : [{}];
      lignes[0] = merge(lignes[0] || {});
      const patch = { ...zone, lignes };
      if (!zone.name && res.zone_name) patch.name = res.zone_name;
      if (res.observations) patch.observations = [zone.observations, res.observations].filter(Boolean).join("\n");
      changeZone(zone.id, patch);
    } else {
      const pid = res.prestation || activePrest;
      if (!pid || !allPrestations[pid]) { toast.error("Prestation non déterminée — ouvrez la prestation puis dictez dans une zone."); return; }
      const prestations = visit.prestations.includes(pid) ? visit.prestations : [...visit.prestations, pid];
      const zone = { id: crypto.randomUUID(), name: res.zone_name || "", prestation: pid, lignes: [merge({})], observations: res.observations || "", photo_ids: [] };
      update({ prestations, zones: [...(visit.zones || []), zone] });
      setActivePrest(pid);
    }
    toast.success("Relevé appliqué depuis la dictée");
    setDictateCtx(null);
  };

  const setConstraint = (k, v) => update({ constraints: { ...(visit.constraints || {}), [k]: v } });  const toggleMean = (m) => {
    const cur = visit.constraints?.moyens_acces || [];
    setConstraint("moyens_acces", cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  };

  // ----- photos -----
  const compressImage = (file) => new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const max = 1600;
        let { width, height } = img;
        if (width > max || height > max) { const r = Math.min(max / width, max / height); width *= r; height *= r; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" })), "image/jpeg", 0.82);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const off = !offlineSync.isOnline();
    for (const file of files) {
      const compressed = await compressImage(file);
      if (off) {
        const dataUrl = await fileToDataUrl(compressed);
        offlineSync.enqueuePhoto(id, dataUrl, compressed.name, "", photoZone);
        setVisit((p) => ({ ...p, photos: [...(p.photos || []), { id: `local-${crypto.randomUUID()}`, caption: "", zone_id: photoZone || null, _localUrl: dataUrl, _pending: true }] }));
      } else {
        try {
          const fd = new FormData();
          fd.append("file", compressed);
          fd.append("caption", "");
          if (photoZone) fd.append("zone_id", photoZone);
          await api.post(`/visits/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) {
          const dataUrl = await fileToDataUrl(compressed);
          offlineSync.enqueuePhoto(id, dataUrl, compressed.name, "", photoZone);
        }
      }
    }
    if (!off) { const { data } = await api.get(`/visits/${id}`); setVisit((p) => ({ ...p, photos: data.photos })); }
    setUploading(false);
    if (uploadRef.current) uploadRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
    if (off) toast.warning("Photo(s) mise(s) en file — envoi au retour du réseau");
    else toast.success("Photo(s) ajoutée(s)");
  };

  const updateCaption = async (fileId, caption) => {
    setVisit((p) => ({ ...p, photos: p.photos.map((f) => f.id === fileId ? { ...f, caption } : f) }));
    try { await api.patch(`/files/${fileId}`, { caption }); } catch (e) { toast.error(apiError(e)); }
  };
  const deletePhoto = async (fileId) => {
    try { await api.delete(`/files/${fileId}`); const { data } = await api.get(`/visits/${id}`); setVisit(data); toast.success("Photo supprimée"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setCover = async (fileId) => {
    try {
      const { data } = await api.post(`/visits/${id}/photos/${fileId}/set-cover`);
      setVisit((p) => ({ ...p, site_cover_file_id: data.cover_file_id }));
      toast.success(data.cover_file_id ? "Photo de couverture définie — visible dans l'espace client" : "Photo de couverture retirée");
    } catch (e) { toast.error(apiError(e)); }
  };

  // ----- workflow -----
  const doAction = async (fn, okMsg) => {
    try { const { data } = await fn(); setVisit((p) => ({ ...p, ...data })); toast.success(okMsg); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submit = () => doAction(() => api.post(`/visits/${id}/submit`, { reply: completionReply }), "Visite soumise pour vérification");
  const validate = () => doAction(() => api.post(`/visits/${id}/validate`), "Visite validée");
  const publishClient = () => doAction(() => api.post(`/visits/${id}/publish-client`), visit?.client_published ? "Retiré de l'espace client" : "Publié dans l'espace client");
  const requestCompletion = () => doAction(() => api.post(`/visits/${id}/request-completion`, { comment: reviewComment }), "Compléments demandés");
  const reopen = () => doAction(() => api.post(`/visits/${id}/reopen`, { comment: reviewComment }), "Visite rouverte");
  const assign = (uid) => doAction(() => api.post(`/visits/${id}/assign`, { assigned_to: uid }), "Visite affectée");
  const deleteVisit = async () => {
    try { await api.delete(`/visits/${id}`); toast.success("Visite supprimée"); navigate("/check"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const missing = missingPoints(visit);
  const selectedPrests = allPrestationList.filter((p) => visit.prestations?.includes(p.id));

  // aggregation for synthesis
  const agg = {};
  (visit.zones || []).forEach((z) => {
    const pid = z.prestation; if (!pid) return;
    const b = agg[pid] || (agg[pid] = {});
    z.lignes.forEach((l) => {
      let unit, val, estimee = false;
      if (pid === "vitres") { unit = "m²"; val = surfaceVitres(l); }
      else if (pid === "gouttieres") { unit = "ml"; val = parseFloat(String(l.longueur ?? "").toString().replace(",", ".")); }
      else if (pid === "depoussierage") { unit = l.unite || "U"; val = parseFloat(String(l.quantite ?? "").replace(",", ".")); }
      else if (pid === "sols_facades") { unit = "m²"; val = surfaceSols(l); estimee = l.mesure === "estimée"; }
      else if (pid === "balisage") { unit = l.unite || "U"; val = parseFloat(String(l.quantite ?? "").replace(",", ".")); }
      else { unit = l.unite || "U"; val = parseFloat(String(l.quantite ?? "").replace(",", ".")); }
      const key = unit || "U";
      const t = b[key] || (b[key] = { value: 0, estimee: 0, incomplet: 0 });
      if (val == null || Number.isNaN(val)) t.incomplet += 1;
      else { t.value += val; if (estimee) t.estimee += val; }
    });
  });

  return (
    <div className="space-y-5" data-testid="visit-editor-page">
      <button onClick={() => navigate("/check")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-visits">
        <Icons.ChevronLeft className="h-4 w-4" /> SIGNA CHECK
      </button>

      {/* Header */}
      <Card className="p-4 lg:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-xl font-bold text-slate-900 lg:text-2xl">Visite avant devis</h1>
              <StatusBadge status={visit.status} />
            </div>
            <p className="mt-1 text-sm text-slate-600">
              <span className="font-mono-data text-xs text-slate-400">{visit.reference}</span> · {visit.client_name} — {visit.site_name}
            </p>
            <p className="text-sm text-slate-500">{fmtDate(visit.date)} · {visit.author_name}</p>
            {visit.imported_from_pdf && <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800" data-testid="visit-imported-badge"><Icons.FileUp className="h-3.5 w-3.5" />Importée depuis un PDF{visit.source_reference ? ` · réf. d'origine ${visit.source_reference}` : ""}{visit.source_author_name ? ` · auteur d'origine ${visit.source_author_name}` : ""}</p>}
          </div>
          <div className="flex flex-col items-end gap-1">
            <SyncBadge state={sync} />
            {pending > 0 && <span className="text-[11px] text-slate-400" data-testid="pending-count">{pending} modif. en attente</span>}
          </div>
        </div>

        {visit.status === "a_completer" && visit.review_comment && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800" data-testid="review-comment">
            <Icons.MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0" />
            <div><span className="font-semibold">Compléments demandés : </span>{visit.review_comment}</div>
          </div>
        )}
        {!canManage && visit.status === "a_completer" && (
          <div className="mt-2 space-y-1.5" data-testid="completion-reply-block">
            <Label className="text-xs text-slate-600">Votre réponse (jointe à la resoumission)</Label>
            <Textarea value={completionReply} onChange={(e) => setCompletionReply(e.target.value)} placeholder="Répondez aux compléments demandés…" data-testid="completion-reply-input" />
          </div>
        )}
        {canManage && visit.status === "a_verifier" && visit.completion_reply && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="completion-reply">
            <Icons.MessageSquareReply className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{visit.review_comment && <div className="mb-1 text-emerald-900/70"><span className="font-semibold">Compléments demandés : </span>{visit.review_comment}</div>}<span className="font-semibold">Réponse du commercial : </span>{visit.completion_reply}</div>
          </div>
        )}
        {readOnly && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-100 p-2.5 text-sm text-slate-600" data-testid="readonly-banner">
            <Icons.Lock className="h-4 w-4" /> Visite {visit.status === "validee" ? "validée" : "en vérification"} — lecture seule pour votre rôle.
          </div>
        )}
      </Card>

      {/* Step tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1">
        {STEPS.map((s) => (
          <button key={s.id} onClick={() => setStep(s.id)} data-testid={`step-${s.id}`}
            className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${step === s.id ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Step: Site */}
      {step === "site" && (
        <div className="space-y-5">
          <Card className="p-5">
            <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">Informations de la visite</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Client</Label><Input value={visit.client_name || ""} disabled /></div>
              <div className="space-y-1.5"><Label>Site</Label><Input value={visit.site_name || ""} disabled /></div>
              <div className="space-y-1.5"><Label>Date de visite</Label>
                <DatePicker value={visit.date || ""} onChange={(v) => update({ date: v })} disabled={readOnly} testId="visit-date" /></div>
              <div className="space-y-1.5"><Label>Heure (programmation)</Label>
                <Input type="time" value={visit.scheduled_time || ""} onChange={(e) => update({ scheduled_time: e.target.value })} disabled={readOnly} data-testid="visit-time" /></div>
            </div>
          </Card>
          <Card className="p-5">
            <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Prestations concernées</h2>
            <p className="mb-3 text-sm text-slate-500">Sélectionnez les prestations relevées lors de cette visite.</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {allPrestationList.filter((p) => !p.legacy || visit.prestations?.includes(p.id)).map((p) => {
                const active = visit.prestations?.includes(p.id);
                return (
                  <button key={p.id} disabled={readOnly} onClick={() => togglePrestation(p.id)} data-testid={`toggle-prest-${p.id}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition-colors ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    <PrestIcon name={p.icon} className="h-4 w-4" /> {p.name}
                    {active && <Icons.Check className="ml-auto h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </Card>
          <Card className="p-5" data-testid="equipments-card">
            <h2 className="mb-1 font-display text-lg font-semibold text-slate-800">Équipements / moyens d'accès utilisés</h2>
            <p className="mb-3 text-sm text-slate-500">Cochez les équipements employés sur cette visite (affichés sur le rapport).</p>
            <div className="flex flex-wrap gap-2">
              {Array.from(new Set([...(catalog.equipments || []), ...equipments])).map((name) => {
                const active = equipments.includes(name);
                return (
                  <button key={name} disabled={readOnly} onClick={() => toggleEquip(name)} data-testid={`toggle-equip-${name}`}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {active && <Icons.Check className="h-3.5 w-3.5" />}{name}
                  </button>
                );
              })}
            </div>
            {!readOnly && (
              <div className="mt-3 flex gap-2">
                <Input placeholder="Ajouter un équipement…" data-testid="equip-custom-input"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      const name = e.target.value.trim();
                      if (!equipments.includes(name)) update({ equipments_used: [...equipments, name] });
                      e.target.value = "";
                    }
                  }} />
              </div>
            )}
          </Card>
        </div>
      )}
      {step === "releves" && (
        <div className="space-y-4">
          {selectedPrests.length === 0 ? (
            <Card className="p-8 text-center text-slate-500" data-testid="no-prestation">Sélectionnez d'abord une prestation à l'étape « Site ».</Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {selectedPrests.map((p) => (
                  <button key={p.id} onClick={() => setActivePrest(p.id)} data-testid={`prest-tab-${p.id}`}
                    className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium ${activePrest === p.id ? "border-[#0D2E24] bg-[#0D2E24] text-white" : "border-slate-200 bg-white text-slate-600"}`}>
                    <PrestIcon name={p.icon} className="h-4 w-4" /> {p.name}
                    <span className="ml-1 rounded-full bg-black/10 px-1.5 text-xs">{zonesFor(p.id).length}</span>
                  </button>
                ))}
                {!readOnly && (
                  <Button variant="outline" size="sm" onClick={() => setDictateCtx({ mode: "visit", prestation: activePrest })}
                    className="ml-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="visit-dictate-btn">
                    <Icons.Mic className="mr-1.5 h-4 w-4" /> Dicter un relevé
                  </Button>
                )}
              </div>
              {activePrest && (
                <div className="space-y-3">
                  {allPrestations[activePrest]?.surface && <SurfaceTools prest={activePrest} zones={visit.zones || []} disabled={readOnly}
                    onAdd={(zs) => setZones([...(visit.zones || []), ...zs])} />}
                  {zonesFor(activePrest).map((z) => allPrestations[activePrest]?.surface ? (
                    <SurfaceZoneCard key={z.id} zone={z} disabled={readOnly} onChange={(next) => changeZone(z.id, next)}
                      onDuplicate={() => duplicateZone(z)} onDelete={() => deleteZone(z.id)} />
                  ) : (
                    <ZoneCard key={z.id} zone={z} def={allPrestations[activePrest]} disabled={readOnly}
                      onChange={(next) => changeZone(z.id, next)} onDuplicate={() => duplicateZone(z)} onDelete={() => deleteZone(z.id)}
                      onDictate={() => setDictateCtx({ mode: "zone", zoneId: z.id, prestation: z.prestation })} />
                  ))}
                  {zonesFor(activePrest).length === 0 && (
                    <Card className="p-6 text-center text-sm text-slate-500">Aucune zone pour cette prestation.</Card>
                  )}
                  {!readOnly && (
                    <Button variant="outline" onClick={() => addZone(activePrest)} data-testid="add-zone-btn" className="w-full border-dashed">
                      <Icons.Plus className="mr-1.5 h-4 w-4" /> Ajouter une zone
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step: Contraintes & Photos */}
      {step === "contraintes" && (
        <div className="space-y-5">
          <Card className="p-5">
            <h2 className="mb-4 font-display text-lg font-semibold text-slate-800">Contraintes générales du site</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Point d'eau</Label><TriState value={visit.constraints?.point_eau} onChange={(v) => setConstraint("point_eau", v)} testId="constraint-eau" /></div>
              <div className="space-y-1.5"><Label>Électricité</Label><TriState value={visit.constraints?.electricite} onChange={(v) => setConstraint("electricite", v)} testId="constraint-elec" /></div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Moyens d'accès envisagés</Label>
              <div className="flex flex-wrap gap-2">
                {ACCESS_MEANS.map((m) => {
                  const active = (visit.constraints?.moyens_acces || []).includes(m.v);
                  return <button key={m.v} disabled={readOnly} onClick={() => toggleMean(m.v)} data-testid={`mean-${m.v}`}
                    className={`rounded-full border px-3 py-1.5 text-sm ${active ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`}>{m.l}</button>;
                })}
              </div>
              <p className="text-xs text-slate-400">Observations pour préparer le devis, pas des recommandations automatiques de sécurité.</p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {[["horaires", "Horaires possibles"], ["occupation", "Occupation & circulation du public"], ["stationnement", "Stationnement & distance de manutention"], ["restrictions", "Restrictions signalées par le client"], ["risques", "Risques ou difficultés observés"], ["infos_a_confirmer", "Informations restant à confirmer"]].map(([k, l]) => (
                <div key={k} className="space-y-1.5"><Label className="text-sm">{l}</Label>
                  <Textarea rows={2} value={visit.constraints?.[k] || ""} onChange={(e) => setConstraint(k, e.target.value)} disabled={readOnly} data-testid={`constraint-${k}`} /></div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-semibold text-slate-800">Photos du site</h2>
              {!readOnly && (
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                  <Select value={photoZone || "visit"} onValueChange={(v) => setPhotoZone(v === "visit" ? "" : v)}>
                    <SelectTrigger className="w-full sm:w-44" data-testid="photo-zone-select"><SelectValue placeholder="Rattacher à…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visit">Visite (générale)</SelectItem>
                      {(visit.zones || []).map((z) => <SelectItem key={z.id} value={z.id}>{z.name || "Zone"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => uploadRef.current?.click()} disabled={uploading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 sm:flex-none" data-testid="add-photo-btn">
                    {uploading ? <Icons.Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icons.Camera className="mr-1.5 h-4 w-4" />} Prendre une photo
                  </Button>
                  <Button variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading} className="flex-1 sm:flex-none" data-testid="add-gallery-btn">
                    <Icons.Images className="mr-1.5 h-4 w-4" /> Galerie
                  </Button>
                  <input ref={uploadRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleUpload} data-testid="photo-input" />
                  <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="gallery-input" />
                </div>
              )}
            </div>
            {(!visit.photos || visit.photos.length === 0) ? (
              <p className="mt-4 text-sm text-slate-500">Aucune photo pour le moment.</p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {visit.photos.map((f, idx) => {
                  const zoneName = (visit.zones || []).find((z) => z.id === f.zone_id)?.name;
                  const src = f._localUrl || fileUrl(f.id);
                  const gallery = visit.photos.map((p) => ({ src: p._localUrl || fileUrl(p.id), caption: p.caption }));
                  return (
                    <div key={f.id} className="overflow-hidden rounded-lg border border-slate-200" data-testid={`photo-${f.id}`}>
                      <div className="relative">
                        <img src={src} alt={f.caption || f.original_filename} onClick={() => lb.open(gallery, idx)} className="h-32 w-full cursor-zoom-in object-cover" />
                        {f._pending && <span className="absolute left-1 top-1 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium text-white">En attente</span>}
                        {!readOnly && !f._pending && (
                          <button onClick={() => setCover(f.id)} title="Définir comme photo de couverture du site"
                            className={`absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full transition-colors ${visit.site_cover_file_id === f.id ? "bg-amber-400 text-white" : "bg-black/40 text-white hover:bg-black/60"}`}
                            data-testid={`set-cover-${f.id}`}>
                            <Icons.Star className="h-4 w-4" fill={visit.site_cover_file_id === f.id ? "currentColor" : "none"} />
                          </button>
                        )}
                        {visit.site_cover_file_id === f.id && <span className="absolute bottom-1 left-1 rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid={`cover-badge-${f.id}`}>Couverture</span>}
                      </div>
                      <div className="p-2">
                        {zoneName && <span className="mb-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{zoneName}</span>}
                        <Input value={f.caption || ""} onChange={(e) => updateCaption(f.id, e.target.value)} placeholder="Légende…" disabled={readOnly || f._pending} className="h-8 text-xs" data-testid={`caption-${f.id}`} />
                        {!readOnly && !f._pending && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><button className="mt-1 text-[11px] text-rose-500 hover:underline" data-testid={`delete-photo-${f.id}`}>Supprimer</button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deletePhoto(f.id)} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="mt-3 text-xs text-slate-400">Les photos sont privées et accessibles uniquement aux utilisateurs autorisés. Compression automatique appliquée.</p>
          </Card>
          {lb.node}
        </div>
      )}

      {/* Step: Synthèse */}
      {step === "synthese" && (
        <div className="space-y-5">
          <Card className="p-5">
            <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">Synthèse des quantités</h2>
            {Object.keys(agg).length === 0 ? <p className="text-sm text-slate-500">Aucune quantité relevée.</p> : (
              <div className="space-y-2" data-testid="synthesis-quantities">
                {Object.entries(agg).map(([pid, totals]) => (
                  <div key={pid} className="flex flex-wrap items-baseline gap-x-3 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="font-semibold text-slate-700">{allPrestations[pid]?.name || pid}</span>
                    {Object.entries(totals).map(([unit, t]) => (
                      <span key={unit} className="font-mono-data text-sm text-emerald-700">
                        {fmtNum(t.value)} {unit}
                        {t.estimee > 0 && <span className="text-amber-600"> (dont {fmtNum(t.estimee)} estimé)</span>}
                        {t.incomplet > 0 && <span className="text-rose-500"> · {t.incomplet} incomplet(s)</span>}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-slate-400">Les relevés ne constituent pas une certification de conformité réglementaire.</p>
          </Card>

          <Card className="p-5">
            <h2 className="mb-3 font-display text-lg font-semibold text-slate-800">Observations & notes</h2>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Observations générales</Label>
                <Textarea rows={3} value={visit.observations || ""} onChange={(e) => update({ observations: e.target.value })} disabled={readOnly} data-testid="visit-observations" /></div>
              <div className="space-y-1.5"><Label>Informations à confirmer</Label>
                <Textarea rows={2} value={visit.info_to_confirm || ""} onChange={(e) => update({ info_to_confirm: e.target.value })} disabled={readOnly} data-testid="visit-info-confirm" /></div>
              <div className="space-y-1.5 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                <Label className="flex items-center gap-1.5 text-amber-800"><Icons.Lock className="h-3.5 w-3.5" /> Notes internes (chiffrage) — exclues du rapport client</Label>
                <Textarea rows={2} value={visit.internal_notes || ""} onChange={(e) => update({ internal_notes: e.target.value })} disabled={readOnly} data-testid="visit-internal-notes" />
              </div>
            </div>
          </Card>

          {missing.length > 0 && (
            <Card className="border-amber-200 p-5" data-testid="missing-points">
              <h3 className="mb-2 flex items-center gap-2 font-display font-semibold text-amber-700"><Icons.AlertTriangle className="h-4 w-4" /> Points manquants ({missing.length})</h3>
              <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
                {missing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
              <p className="mt-2 text-xs text-slate-400">Vous pouvez enregistrer un brouillon incomplet. Indiquez « à confirmer » pour les inconnues explicites.</p>
            </Card>
          )}

          {/* Actions */}
          <Card className="p-5">
            <h3 className="mb-3 font-display font-semibold text-slate-800">Actions</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => navigate(`/report/${id}`)} data-testid="goto-report">
                <Icons.FileText className="mr-1.5 h-4 w-4" /> Aperçu du rapport
              </Button>
              <Button variant="outline" onClick={exportSynthesis} data-testid="export-synthesis">
                <Icons.Sheet className="mr-1.5 h-4 w-4" /> Export tableur
              </Button>

              {!canManage && ["brouillon", "a_completer"].includes(visit.status) && (
                <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-visit">
                  <Icons.Send className="mr-1.5 h-4 w-4" /> Soumettre pour vérification
                </Button>
              )}

              {canManage && (
                <>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-slate-500">Affecter à</Label>
                    <Select value={visit.assigned_to || ""} onValueChange={assign}>
                      <SelectTrigger className="w-52" data-testid="assign-select"><SelectValue placeholder="Choisir un commercial" /></SelectTrigger>
                      <SelectContent>{commercials.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {["brouillon", "a_completer", "a_verifier"].includes(visit.status) && (
                    <>
                      <Button onClick={validate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="validate-visit">
                        <Icons.CircleCheck className="mr-1.5 h-4 w-4" /> Valider
                      </Button>
                      {visit.status === "a_verifier" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="outline" className="text-orange-600" data-testid="request-completion-btn"><Icons.MessageSquareWarning className="mr-1.5 h-4 w-4" />Demander des compléments</Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Demander des compléments</AlertDialogTitle>
                            <AlertDialogDescription>Indiquez ce qui doit être complété par le commercial.</AlertDialogDescription></AlertDialogHeader>
                          <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Ex. Confirmer la hauteur de la charpente…" data-testid="completion-comment" />
                          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={requestCompletion} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-request-completion">Envoyer</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </>
                  )}
                  {visit.status === "validee" && (
                    <>
                      <Button variant="outline" onClick={reopen} data-testid="reopen-visit"><Icons.Unlock className="mr-1.5 h-4 w-4" />Rouvrir la visite</Button>
                      <Button variant="outline" onClick={publishClient} data-testid="publish-client-btn"
                        className={visit.client_published ? "border-emerald-300 text-emerald-700" : ""}>
                        <Icons.Globe className="mr-1.5 h-4 w-4" />{visit.client_published ? "Publié au client ✓" : "Publier au client"}
                      </Button>
                    </>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="outline" className="text-rose-600" data-testid="delete-visit-btn"><Icons.Trash2 className="mr-1.5 h-4 w-4" />Supprimer la visite</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Supprimer définitivement cette visite ?</AlertDialogTitle>
                        <AlertDialogDescription>La visite {visit.reference}, ses relevés et ses photos seront définitivement supprimés. Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteVisit} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-visit">Supprimer</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      {canManage && visit && <VisitReportCard visit={visit} />}

      <DictationDialog
        open={!!dictateCtx}
        onOpenChange={(o) => { if (!o) setDictateCtx(null); }}
        prestation={dictateCtx?.prestation || null}
        existingLigne={dictateCtx?.mode === "zone" ? ((visit.zones || []).find((z) => z.id === dictateCtx.zoneId)?.lignes?.[0] || {}) : {}}
        onApply={applyDictation}
      />

      <AlertDialog open={conflict} onOpenChange={setConflict}>
        <AlertDialogContent data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Conflit de modifications</AlertDialogTitle>
            <AlertDialogDescription>
              Cette visite a été modifiée ailleurs pendant votre édition. Choisissez la version à conserver
              pour éviter d'écraser des données sans le savoir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={resolveUseServer} data-testid="conflict-use-server">
              Recharger la version du serveur
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={resolveKeepMine} data-testid="conflict-keep-mine">
              Conserver ma version
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
